import * as React from "react";
import * as Babel from "@babel/standalone";
import * as lucideReactLibrary from "lucide-react";
import axios from "axios";
import * as cheerio from "cheerio";
import { Bluelagoon } from "@bluelagoon";
import * as ComponentLibrary from "./componentLibrary";
import _ from "lodash";
import * as Recharts from "recharts";
import * as simpleStatistics from "simple-statistics";

/**
 * Injects all core-js polyfill policies before code translation
 * This ensures that all necessary polyfills are available during Babel transformation
 * @param {string} code - The source code to inject policies into
 * @returns {string} - The code with injected polyfill policies
 */
const injectCoreJsPolicies = (code) => {
    // Create policy injection code that directly injects the imported polyfills
    const policyInjectionCode = `
        // Core-js polyfill policies injection
        (function() {
            const global = typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : this;
            
            // Inject polyfills if they don't exist
            if (!global.Array || !global.Array.from) {
                if (global.Array) {
                    global.Array.from = ArrayFrom;
                }
            }
            
            if (!global.Array || !global.Array.prototype || !global.Array.prototype.find) {
                if (global.Array && global.Array.prototype) {
                    global.Array.prototype.find = ArrayFind;
                }
            }
            
            if (!global.Array || !global.Array.prototype || !global.Array.prototype.findIndex) {
                if (global.Array && global.Array.prototype) {
                    global.Array.prototype.findIndex = ArrayFindIndex;
                }
            }
            
            if (!global.Array || !global.Array.prototype || !global.Array.prototype.includes) {
                if (global.Array && global.Array.prototype) {
                    global.Array.prototype.includes = ArrayIncludes;
                }
            }
            
            if (!global.Object || !global.Object.assign) {
                if (global.Object) {
                    global.Object.assign = ObjectAssign;
                }
            }
            
            if (!global.Object || !global.Object.keys) {
                if (global.Object) {
                    global.Object.keys = ObjectKeys;
                }
            }
            
            if (!global.Object || !global.Object.values) {
                if (global.Object) {
                    global.Object.values = ObjectValues;
                }
            }
            
            if (!global.Object || !global.Object.entries) {
                if (global.Object) {
                    global.Object.entries = ObjectEntries;
                }
            }
            
            if (!global.String || !global.String.prototype || !global.String.prototype.includes) {
                if (global.String && global.String.prototype) {
                    global.String.prototype.includes = StringIncludes;
                }
            }
            
            if (!global.String || !global.String.prototype || !global.String.prototype.startsWith) {
                if (global.String && global.String.prototype) {
                    global.String.prototype.startsWith = StringStartsWith;
                }
            }
            
            if (!global.String || !global.String.prototype || !global.String.prototype.endsWith) {
                if (global.String && global.String.prototype) {
                    global.String.prototype.endsWith = StringEndsWith;
                }
            }
            
            if (!global.Promise) {
                global.Promise = Promise;
            }
            
            if (!global.Map) {
                global.Map = Map;
            }
            
            if (!global.Set) {
                global.Set = Set;
            }
            
            if (!global.Symbol) {
                global.Symbol = Symbol;
            }
            
            if (!global.WeakMap) {
                global.WeakMap = WeakMap;
            }
            
            if (!global.WeakSet) {
                global.WeakSet = WeakSet;
            }
        })();
    `;

    // Inject policies at the beginning of the code
    return policyInjectionCode + "\n" + code;
};

// Create a configured axios instance with default headers
const createConfiguredAxios = (componentConfig) => {
    const instance = axios.create({
        timeout: 60 * 15 * 1000, // 15 minutes default timeout
    });

    // Add request interceptor to include x-api-key header
    instance.interceptors.request.use((requestConfig) => {
        if (componentConfig?.apiKey) {
            requestConfig.headers = requestConfig.headers || {};
            requestConfig.headers["x-api-key"] = componentConfig.apiKey;
            if (Bluelagoon.getToken()) {
                requestConfig.headers[
                    "Authorization"
                ] = `Bearer ${Bluelagoon.getToken()}`;
            }
        }
        return requestConfig;
    });

    return instance;
};

const lucideReactLibrary2 = {
    ...lucideReactLibrary,
    Icon: ComponentLibrary.Icon || (() => null), // Fallback if Icon is not available
};

// eslint-disable-next-line no-undef
const lucideReact = new Proxy(lucideReactLibrary2, {
    get(target, prop) {
        if (prop in target) {
            return target[prop];
        }
        // Return a styled question mark component
        return (props = {}) =>
            React.createElement(
                "span",
                {
                    ...props,
                    style: {
                        display: "inline-block",
                        width: "1em",
                        height: "1em",
                        textAlign: "center",
                        fontWeight: "bold",
                        ...props.style,
                    },
                },
                "?"
            );
    },
});

const ComponentWrapper = ({ children, config }) => {
    const containerRef = React.useRef(null);
    const observerRef = React.useRef(null);
    const processedUrls = React.useRef(new Map());

    const processMediaSources = React.useCallback(
        async (elements, mediaType) => {
            elements.forEach((element) => {
                // Skip if we've already processed this exact URL or if it already contains a signature
                if (
                    element.src &&
                    element.src.includes("aws.com") &&
                    !processedUrls.current.get(element.src) &&
                    !element.src.includes("X-Amz-Signature=") && // Skip if already signed
                    !element.src.includes("Signature=")
                ) {
                    const proxyUrl = Bluelagoon.getProxyImageUrl();

                    // Mark this URL as being processed
                    processedUrls.current.set(element.src, element.src);
                    const originalSrc = element.src;

                    // Create configured axios instance with API key
                    const configuredAxios = createConfiguredAxios(config);

                    // First get the signed URL
                    configuredAxios
                        .get(`${proxyUrl}`, {
                            params: {
                                fileUrl: element.src,
                            },
                        })
                        .then((response) => {
                            if (response.data && response.data.url) {
                                // Only update if the element hasn't changed to a different src while we were fetching
                                if (element.src === originalSrc) {
                                    element.src = response.data.url;
                                    processedUrls.current.set(
                                        originalSrc,
                                        response.data.url
                                    );
                                }
                            }
                        })
                        .catch((error) => {
                            console.error(
                                `Error getting signed URL for ${mediaType}:`,
                                error
                            );
                            // Remove from processed set if there was an error
                            processedUrls.current.delete(originalSrc);
                        });
                } else if (processedUrls.current.get(element.src)) {
                    if (
                        element.src !== processedUrls.current.get(element.src)
                    ) {
                        element.src = processedUrls.current.get(element.src);
                    }
                }
            });
        },
        [config?.apiKey]
    );

    const processImages = React.useCallback(
        (images) => processMediaSources(images, "image"),
        [processMediaSources]
    );

    const processVideos = React.useCallback(
        (videos) => processMediaSources(videos, "video"),
        [processMediaSources]
    );

    React.useEffect(() => {
        if (containerRef.current && config?.apiKey) {
            // Process existing images and videos
            const images = containerRef.current.querySelectorAll("img");
            const videos = containerRef.current.querySelectorAll("video");

            processImages(images);
            processVideos(videos);

            // Set up observer for new media elements and attribute changes
            observerRef.current = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                    if (
                        mutation.type === "attributes" &&
                        mutation.attributeName === "src"
                    ) {
                        // Handle src attribute change for images and videos
                        if (mutation.target.nodeName === "IMG") {
                            processImages([mutation.target]);
                        } else if (mutation.target.nodeName === "VIDEO") {
                            processVideos([mutation.target]);
                        }
                    } else if (mutation.type === "childList") {
                        // Check for new nodes
                        mutation.addedNodes.forEach((node) => {
                            // If the added node is an image or video
                            if (node.nodeName === "IMG") {
                                processImages([node]);
                            } else if (node.nodeName === "VIDEO") {
                                processVideos([node]);
                            }
                            // If the added node might contain images or videos
                            else if (node.nodeType === 1) {
                                // ELEMENT_NODE
                                const newImages = node.querySelectorAll("img");
                                const newVideos =
                                    node.querySelectorAll("video");

                                if (newImages.length > 0) {
                                    processImages(newImages);
                                }
                                if (newVideos.length > 0) {
                                    processVideos(newVideos);
                                }
                            }
                        });
                    }
                });
            });

            // Start observing with attribute changes
            observerRef.current.observe(containerRef.current, {
                childList: true,
                subtree: true,
                attributes: true,
                attributeFilter: ["src"], // Only watch for src attribute changes
            });
        }

        // Cleanup
        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
            // Clear the processed URLs set
            processedUrls.current.clear();
        };
    }, [children, config, processImages, processVideos]);

    return <div ref={containerRef}>{children}</div>;
};

class ComponentErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            shouldRender: true,
            didReportError: false,
        };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error, shouldRender: false };
    }

    componentDidCatch(error, errorInfo) {
        console.error("Component Error:", error, errorInfo);

        // Check if it's an undefined icon error
        if (
            error.message &&
            error.message.includes("Element type is invalid") &&
            error.message.includes("undefined")
        ) {
            console.warn("Undefined icon detected, rendering without icon");
            // Don't report this as a critical error, just render without the icon
            return;
        }

        const lineInfo = error.stack
            ?.split("\n")[1]
            ?.match(/\(.*:(\d+):(\d+)\)/);
        const errorWithLine = lineInfo
            ? `${error.message} (line ${lineInfo[1]})`
            : error.message;
        if (!this.state.didReportError) {
            this.props.onError?.(errorWithLine, true);
            this.setState({ shouldRender: false, didReportError: true });
        }
    }

    render() {
        if (!this.state.shouldRender || this.state.hasError) {
            const lineInfo = this.state.error?.stack
                ?.split("\n")[1]
                ?.match(/\(.*:(\d+):(\d+)\)/);
            const errorMessage = lineInfo
                ? `${this.state.error?.message} (line ${lineInfo[1]})`
                : this.state.error?.message;
            return (
                <div className="text-red-500 p-4 bg-red-50 rounded">
                    Error rendering component: {errorMessage}
                </div>
            );
        }

        return this.props.children;
    }
}

/**
 * RemoteComponentRenderer - Fetches and renders remote components with intercepted console logging
 * @param {Object} props - Component props
 * @param {string} props.componentUrl - URL to fetch the component from
 * @param {Object} props.responseData - Data to pass to the component
 * @param {Function} props.onError - Error handler function
 * @param {Function} props.handleSetLogs - Function to handle log updates
 * @param {number} props.logDebounceMs - Debounce time in milliseconds for log batching
 */
export function RemoteComponentRenderer({
    componentUrl,
    responseData,
    onError,
    handleSetLogs,
    logDebounceMs,
}) {
    const [component, setComponent] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);

    React.useEffect(() => {
        async function fetchComponent() {
            try {
                const response = await fetch(componentUrl);
                if (!response.ok) {
                    throw new Error(
                        `Failed to fetch component: ${response.statusText}`
                    );
                }
                const componentData = await response.json();
                setComponent(componentData?.data);
            } catch (err) {
                console.error("Error fetching component:", err);
                setError(err.message);
                onError?.(err.message, false);
            } finally {
                setLoading(false);
            }
        }

        if (componentUrl) {
            fetchComponent();
        }
    }, [componentUrl, onError]);

    if (loading) {
        return (
            <div className="text-amber-500 p-4 bg-amber-50 rounded">
                Loading remote component...
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-red-500 p-4 bg-red-50 rounded">
                Error loading remote component: {error}
            </div>
        );
    }

    return (
        <GeneratedComponentRenderer
            generatedComponent={component}
            responseData={responseData}
            onError={onError}
            handleSetLogs={handleSetLogs}
            logDebounceMs={logDebounceMs}
        />
    );
}

/**
 * GeneratedComponentRenderer - Renders dynamically generated React components with intercepted console logging
 * @param {Object} props - Component props
 * @param {Object} props.generatedComponent - The generated component to render
 * @param {Object} props.responseData - Data to pass to the component
 * @param {Function} props.onError - Error handler function
 * @param {number} props.retryCount - Number of retry attempts
 * @param {number} props.maxRetries - Maximum number of retry attempts
 * @param {Object} props.enviormentVariablesForInjection - Environment variables to inject
 * @param {Object} props.config - Configuration object
 * @param {Object} props.integrations - Integration objects
 * @param {Object} props.additionalComponents - Additional component library
 * @param {Array} props.stepData - Step data for the component
 * @param {Object|null} props.inputs - Previous step output data (like CODE_EDITOR_V2 inputs)
 * @param {Function} props.onSubmit - Submit handler function
 * @param {Object} props.initialData - Initial data for the component
 * @param {Function} props.handleSetLogs - Function to handle log updates (receives array of logs)
 * @param {number} props.logDebounceMs - Debounce time in milliseconds for log batching (default: 1000)
 * @param {Function} props.createPageUrl - Function to create page URLs
 * @param {Object} props.currentUser - Current logged-in user object (includes groups/teams)
 * @param {Object} props.currentRole - Current user's role object
 */
export function GeneratedComponentRenderer({
    generatedComponent,
    responseData,
    onError,
    retryCount,
    maxRetries,
    enviormentVariablesForInjection = {},
    config = {},
    integrations = {},
    additionalComponents = {},
    stepData = [],
    inputs = null,
    onSubmit,
    initialData,
    handleSetLogs,
    logDebounceMs = 1000,
    createPageUrl,
    currentUser,
    currentRole,
}) {
    const [renderError, setRenderError] = React.useState(null);
    const logsRef = React.useRef([]); // Use ref instead of state to avoid re-renders
    const didReportError = React.useRef(false);
    const debounceTimeoutRef = React.useRef(null);

    // Track internal state changes that might cause re-renders
    const prevLogsLength = React.useRef(logsRef.current.length);
    const prevRenderError = React.useRef(renderError);

    React.useEffect(() => {
        if (logsRef.current.length !== prevLogsLength.current) {
            prevLogsLength.current = logsRef.current.length;
        }
        if (renderError !== prevRenderError.current) {
            prevRenderError.current = renderError;
        }
    });

    // Logging system: Intercept native console methods to collect logs for debounced sending
    // Generated components use the native console.log, console.warn, etc., and all calls are captured
    // This prevents excessive API calls while maintaining real-time logging capabilities

    // Function to add logs to ref (no re-renders!)
    const addLog = React.useCallback((logEntry) => {
        // Add to ref instead of state to avoid re-renders
        logsRef.current = [
            ...logsRef.current,
            {
                ...logEntry,
                timestamp: new Date().toISOString(),
                id: Date.now() + Math.random(), // Unique ID for each log
            },
        ];

        // Trigger debounced callback to parent component
        debouncedSendLogs();
    }, []);

    // Create intercepted console object that captures all console calls
    const interceptedConsole = React.useMemo(() => {
        const originalConsole = { ...console };

        return {
            ...console,
            log: (...args) => {
                originalConsole.log(...args); // Still log to real console
                addLog({
                    level: "info",
                    message: args
                        .map((arg) =>
                            typeof arg === "string"
                                ? arg
                                : JSON.stringify(arg, null, 2)
                        )
                        .join(" "),
                    data: args.length > 1 ? args.slice(1) : null,
                    type: "log",
                });
            },
            warn: (...args) => {
                originalConsole.warn(...args); // Still log to real console
                addLog({
                    level: "warning",
                    message: args
                        .map((arg) =>
                            typeof arg === "string"
                                ? arg
                                : JSON.stringify(arg, null, 2)
                        )
                        .join(" "),
                    data: args.length > 1 ? args.slice(1) : null,
                    type: "log",
                });
            },
            error: (...args) => {
                originalConsole.error(...args); // Still log to real console
                addLog({
                    level: "error",
                    message: args
                        .map((arg) =>
                            typeof arg === "string"
                                ? arg
                                : JSON.stringify(arg, null, 2)
                        )
                        .join(" "),
                    data: args.length > 1 ? args.slice(1) : null,
                    type: "log",
                });
            },
            info: (...args) => {
                originalConsole.info(...args); // Still log to real console
                addLog({
                    level: "info",
                    message: args
                        .map((arg) =>
                            typeof arg === "string"
                                ? arg
                                : JSON.stringify(arg, null, 2)
                        )
                        .join(" "),
                    data: args.length > 1 ? args.slice(1) : null,
                    type: "log",
                });
            },
            debug: (...args) => {
                originalConsole.debug(...args); // Still log to real console
                addLog({
                    level: "debug",
                    message: args
                        .map((arg) =>
                            typeof arg === "string"
                                ? arg
                                : JSON.stringify(arg, null, 2)
                        )
                        .join(" "),
                    data: args.length > 1 ? args.slice(1) : null,
                    type: "log",
                });
            },
        };
    }, [addLog]);

    // Debounced function to send logs from ref
    const debouncedSendLogs = React.useCallback(() => {
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        debounceTimeoutRef.current = setTimeout(() => {
            if (logsRef.current.length > 0 && handleSetLogs) {
                handleSetLogs([...logsRef.current]); // Send copy of logs
                logsRef.current = []; // Clear logs after sending
            }
        }, logDebounceMs);
    }, [handleSetLogs, logDebounceMs]);

    // Cleanup timeout on unmount
    React.useEffect(() => {
        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, []);

    const componentProps = React.useMemo(() => {
        if (!generatedComponent?.props || !responseData) return null;
        return Object.keys(generatedComponent.props).reduce(
            (acc, prop) => {
                acc[prop] = responseData[prop];
                return acc;
            },
            {
                onSubmit,
                initialData,
            }
        );
    }, [generatedComponent?.props, responseData, onSubmit]);

    const require = React.useCallback((module) => {
        if (module === "react") {
            return React;
        }
        return null;
    }, []);

    const RenderGeneratedComponent = React.useMemo(() => {
        if (!generatedComponent?.code || !componentProps) return null;
        try {
            const componentValues = Object.values(additionalComponents);
            const componentNames = Object.keys(additionalComponents);

            const dependencyValues = [
                React,
                React.useState,
                React.useEffect,
                React.useRef,
                React.useMemo,
                React.useCallback,
                React.useContext,
                React.useReducer,
                React.useLayoutEffect,
                React.useImperativeHandle,
                React.useId,
                React.useTransition,
                React.useDeferredValue,
                lucideReact.Search,
                lucideReact,
                createConfiguredAxios(config), // Pass configured axios instance
                cheerio,
                config,
                integrations,
                enviormentVariablesForInjection,
                stepData,
                inputs,
                onSubmit,
                require,
                initialData,
                interceptedConsole, // Pass the intercepted console object
                createPageUrl,
                currentUser,
                currentRole,
                Recharts,
                ...Object.values(ComponentLibrary),
                ...componentValues,
                _,
                simpleStatistics,
            ];

            const dependencyNames = [
                "React",
                "useState",
                "useEffect",
                "useRef",
                "useMemo",
                "useCallback",
                "useContext",
                "useReducer",
                "useLayoutEffect",
                "useImperativeHandle",
                "useId",
                "useTransition",
                "useDeferredValue",
                "Search",
                "lucideReact",
                "axios", // Keep the name as "axios" for compatibility
                "cheerio",
                "config",
                "integrations",
                "process",
                "stepData",
                "inputs",
                "onSubmit",
                "require",
                "initialData",
                "console", // Pass the console object name
                "createPageUrl",
                "currentUser",
                "currentRole",
                "Recharts",
                ...Object.keys(ComponentLibrary),
                ...componentNames,
                "_",
                "simpleStatistics",
            ];

            // Inject core-js policies before transpilation
            const codeWithPolicies = injectCoreJsPolicies(
                generatedComponent.code
            );

            const transpiledCode = Babel.transform(codeWithPolicies, {
                presets: ["react", "env"],
                filename: generatedComponent.name + ".jsx",
            }).code;

            // eslint-disable-next-line no-new-func
            const ComponentFunction = new Function(
                ...dependencyNames,
                `"use strict";
                ${transpiledCode}
                return ${generatedComponent.name};`
            );

            return ComponentFunction(...dependencyValues);
        } catch (error) {
            console.error("Error creating component:", error);
            const lineInfo = error.stack
                ?.split("\n")[1]
                ?.match(/\(.*:(\d+):(\d+)\)/);
            const errorMessage = lineInfo
                ? `${error.message} (line ${lineInfo[1]})`
                : error.message;
            setRenderError(errorMessage);

            if (didReportError.current) {
                return null;
            }

            if (retryCount && maxRetries && retryCount < maxRetries) {
                onError?.(errorMessage, true);
                didReportError.current = true;
            } else {
                didReportError.current = true;
                onError?.(errorMessage, false);
            }

            return null;
        }
    }, [
        generatedComponent,
        componentProps,
        retryCount,
        onError,
        maxRetries,
        config,
        integrations,
        enviormentVariablesForInjection,
        stepData,
        inputs,
        onSubmit,
        additionalComponents,
        initialData,
        createPageUrl,
        currentUser,
        currentRole,
    ]);

    if (renderError) {
        return (
            <div className="text-red-500 p-4 bg-red-50 rounded">
                Error rendering component: {renderError}
            </div>
        );
    }

    if (!componentProps) {
        return (
            <div className="text-amber-500 p-4 bg-amber-50 rounded">
                Loading component data...
            </div>
        );
    }

    if (RenderGeneratedComponent) {
        return (
            <ComponentWrapper config={config}>
                <ComponentErrorBoundary onError={onError}>
                    <RenderGeneratedComponent
                        {...componentProps}
                        config={config}
                        stepData={stepData}
                        inputs={inputs}
                        integrations={integrations}
                        createPageUrl={createPageUrl}
                        currentUser={currentUser}
                        currentRole={currentRole}
                    />
                </ComponentErrorBoundary>
            </ComponentWrapper>
        );
    }

    return null;
}
